/*
  Book an Appointment modal. Rendered once, directly in layout/theme.liquid
  (outside any section), so it can't use the theme's generic <modal-dialog>
  element — that one assumes a `.shopify-section` ancestor (it reads
  `this.closest('.shopify-section').id` on connect) to namespace itself
  per-section, same as the product image modal it was built for. This
  element follows <cart-drawer>'s pattern instead, which is rendered the
  same way and has no such requirement.
*/
class AppointmentModal extends HTMLElement {
  constructor() {
    super();

    const overlay = this.querySelector('[data-appointment-overlay]');
    const closeButton = this.querySelector('[data-appointment-close]');

    if (overlay) overlay.addEventListener('click', this.hide.bind(this));
    if (closeButton) closeButton.addEventListener('click', this.hide.bind(this));

    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
  }

  show(opener) {
    this.openedBy = opener;
    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    if (typeof trapFocus === 'function') {
      trapFocus(this, this.querySelector('[role="dialog"]'));
    }
  }

  hide() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
  }
}
customElements.define('appointment-modal', AppointmentModal);

document.addEventListener('DOMContentLoaded', () => {
  const modal = document.querySelector('appointment-modal');
  if (!modal) return;

  const storeField = document.getElementById('AppointmentForm-store');
  const productField = document.getElementById('AppointmentForm-product');
  const context = document.getElementById('AppointmentModalContext');

  /*
    Shopify's contact notification renders each contact[...] field on its own
    line, but the message body stays blank unless contact[body] is posted, so
    the email arrives as a bare list. Build a readable summary at submit time.
    The markup ships a static fallback value for the no-JS case.
  */
  const form = document.getElementById('AppointmentForm');
  const bodyField = document.getElementById('AppointmentForm-body');

  const valueOf = (id) => {
    const field = document.getElementById(id);
    return field ? field.value.trim() : '';
  };

  /*
    Keys match the metaobject definition's field keys one-for-one, so whatever
    receives this can pass `booking` straight to metaobjectUpsert without
    remapping.
  */
  const readBooking = () => ({
    name: valueOf('AppointmentForm-name'),
    phone: valueOf('AppointmentForm-phone'),
    email: valueOf('AppointmentForm-email'),
    preferred_store: valueOf('AppointmentForm-store'),
    preferred_date: valueOf('AppointmentForm-date'),
    preferred_time: valueOf('AppointmentForm-time'),
    reason_for_visit: valueOf('AppointmentForm-reason'),
    product: valueOf('AppointmentForm-product'),
    status: 'New',
    submitted_at: new Date().toISOString(),
  });

  if (form && bodyField) {
    form.addEventListener('submit', async (event) => {
      const booking = readBooking();
      const shown = (value) => value || '\u2014';

      bodyField.value = [
        'Appointment request from the website.',
        '',
        `Name: ${shown(booking.name)}`,
        `Phone: ${shown(booking.phone)}`,
        `Email: ${shown(booking.email)}`,
        `Preferred store: ${shown(booking.preferred_store)}`,
        `Preferred date: ${shown(booking.preferred_date)}`,
        `Preferred time: ${shown(booking.preferred_time)}`,
        `Reason for visit: ${shown(booking.reason_for_visit)}`,
        `Product: ${shown(booking.product)}`,
      ].join('\n');

      /*
        The contact-form POST navigates the page away immediately, which would
        abort an ordinary fetch — keepalive lets the browser finish this one
        after unload. Fire-and-forget by design: the booking is already being
        emailed by the form itself, so a failing endpoint must never cost the
        customer their submission or show them an error.
      */
      /*
        Same-origin POST to the shop's own /contact endpoint with
        form_type=customer — byte-for-byte what Dawn's newsletter form submits.
        No token, no CORS: this is the shop's own domain. It subscribes the
        booker to email marketing, which is the only first-party way to make
        them reachable by a Shopify Email automation, and that automation is
        what actually sends them the confirmation.
      */
      const consent = document.getElementById('AppointmentForm-consent');
      if (consent && consent.checked && booking.email) {
        /*
          Tags are the only part of the customer record the storefront can
          write — metafields need the Admin API, which browsers can't reach.
          So the booking rides along as tags: staff see it on the customer in
          admin, and Flow can branch on it. `appointment` stays first and
          unprefixed because the Flow condition matches on it.
        */
        const slug = (value) =>
          value
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 60);

        const tags = ['appointment'];
        const tagged = {
          store: booking.preferred_store,
          date: booking.preferred_date,
          time: booking.preferred_time,
          reason: booking.reason_for_visit,
        };

        Object.keys(tagged).forEach((key) => {
          if (tagged[key]) tags.push(`appt-${key}-${slug(tagged[key])}`);
        });

        const params = new URLSearchParams({
          form_type: 'customer',
          utf8: '\u2713',
          'contact[email]': booking.email,
          'contact[first_name]': booking.name,
          'contact[tags]': tags.join(','),
        });

        try {
          fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}contact`, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: params.toString(),
          }).catch(() => {});
        } catch (error) {
          /* Never block the form submit. */
        }
      }

      const endpoint = modal.dataset.endpoint;
      if (endpoint) {
        try {
          fetch(endpoint, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              source: 'appointment-modal',
              shop: window.Shopify ? window.Shopify.shop : '',
              page: window.location.href,
              booking,
            }),
          }).catch(() => {});
        } catch (error) {
          /* Never block the form submit. */
        }
      }

      /*
        DEMO PATH. Writes the booking straight to an `appointment` metaobject
        with an Admin API token rendered into the page. This only works while
        the page is served from the shop's own .myshopify.com origin: there the
        Admin API is same-origin, so no CORS preflight happens. On the live
        domain the request is preflighted, Shopify returns no
        Access-Control-Allow-Origin, and the browser drops it before the token
        is ever sent — hence the guard and the explicit warning.

        Replace this whole block with the app proxy in phase 2; the payload it
        builds is the same one POSTed to data-endpoint.
      */
      const adminToken = modal.dataset.adminToken;
      const onShopOrigin = window.location.hostname.endsWith('.myshopify.com');

      if (adminToken && !onShopOrigin) {
        console.warn(
          `[appointment] Metaobject write skipped: this page is on ${window.location.hostname}. ` +
            'The Admin API is only reachable same-origin, so open the demo on ' +
            `${window.Shopify ? window.Shopify.shop : 'your-shop.myshopify.com'} instead.`
        );
      }

      if (adminToken && onShopOrigin) {
        /*
          Hold the navigation until the entry exists. Fire-and-forget would be
          faster, but an entry that silently fails to appear is the worst thing
          that can happen mid-demo. form.submit() bypasses submit handlers, so
          this doesn't re-enter.
        */
        event.preventDefault();

        const fields = Object.keys(booking)
          .filter((key) => booking[key] !== '')
          .map((key) => ({ key, value: String(booking[key]) }));

        try {
          const response = await fetch(`https://${window.Shopify.shop}/admin/api/2025-10/graphql.json`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Access-Token': adminToken,
            },
            body: JSON.stringify({
              query: `mutation($handle: MetaobjectHandleInput!, $metaobject: MetaobjectUpsertInput!) {
                metaobjectUpsert(handle: $handle, metaobject: $metaobject) {
                  metaobject { id handle }
                  userErrors { field message code }
                }
              }`,
              variables: {
                handle: {
                  type: 'appointment',
                  handle: `appt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                },
                metaobject: { fields },
              },
            }),
          });

          const result = await response.json();
          const errors = result.errors || (result.data && result.data.metaobjectUpsert.userErrors) || [];
          if (errors.length) console.error('[appointment] metaobjectUpsert failed', errors);
        } catch (error) {
          console.error('[appointment] metaobjectUpsert failed', error);
        }

        form.submit();
      }

    });
  }

  /*
    Delegated rather than bound per node: plp.js replaces the grid's entire
    innerHTML on filter/sort and appends fresh cards on infinite scroll, so
    listeners attached to a card at DOMContentLoaded are gone after the first
    facet click — leaving every "Book a Consultation" button on the collection
    page dead. Delegation survives both.
  */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-appointment-trigger]');
    if (trigger) {
      const store = trigger.getAttribute('data-store') || '';
      const product = trigger.getAttribute('data-product') || '';

      if (storeField) {
        const hasStoreOption = Array.from(storeField.options).some((option) => option.value === store);
        storeField.value = hasStoreOption ? store : '';
      }

      if (productField) productField.value = product;

      if (context) {
        if (product) {
          context.textContent = `Regarding: ${product}`;
          context.hidden = false;
        } else {
          context.textContent = '';
          context.hidden = true;
        }
      }

      modal.show(trigger);
    }
  });
});
